const includesScopeLevel = (scopeLevel, requiredScopeLevel) => {
    switch (requiredScopeLevel) {
        case 'write':
            return scopeLevel === 'write' || scopeLevel === 'admin';
        case 'read':
            return (scopeLevel === 'read' ||
                scopeLevel === 'write' ||
                scopeLevel === 'admin');
        case 'admin':
            return scopeLevel === 'admin';
    }
};
const calculateRequiredScopeLevel = (requiredCapabilities) => {
    return requiredCapabilities.reduce((acc, capability) => {
        if (acc === 'admin') {
            return 'admin';
        }
        if (capability.endsWith('-create') ||
            capability.endsWith('-update') ||
            capability.endsWith('-delete')) {
            return 'write';
        }
        else if (!capability.endsWith('-read')) {
            return 'admin';
        }
        return acc;
    }, 'read');
};
export const calculateRequiredScope = ({ resource, requiredCapabilities, }) => ({
    scopeLevel: calculateRequiredScopeLevel(requiredCapabilities),
    resource,
});
export const scopeMatches = ({ scopes, requiredScope, }) => Array.isArray(scopes) &&
    !!scopes.find((s) => (!s.scopeLevel ||
        !requiredScope.scopeLevel ||
        includesScopeLevel(s.scopeLevel, requiredScope.scopeLevel)) &&
        (!s.resource.id ||
            s.resource.id === requiredScope.resource.id ||
            s.resource.id === '*' ||
            requiredScope.resource.id === '*') &&
        (s.resource.type === requiredScope.resource.type ||
            s.resource.type === '*' ||
            requiredScope.resource.type === '*'));
export const getPublicPostReadScope = (postId) => ({
    scopeLevel: 'read',
    resource: { type: 'posts', id: postId },
});
/**
 * Posts with `public` visibility — and posts in groups that grant the needed
 * *-read caps to `none` — are readable without auth; grant read scope for
 * that post id (same pattern as {@link withPublicGroupReadScopes}).
 */
export const withPublicPostReadScopes = (authData, post, neededCapabilities) => {
    if (!neededCapabilities.every((c) => c.endsWith('-read'))) {
        return authData;
    }
    const publicGroupPost = post.visibility === 'group' &&
        neededCapabilities.every((c) => post.group?.capabilities?.none?.add?.includes(c) ?? false);
    if (post.visibility !== 'public' && !publicGroupPost) {
        return authData;
    }
    const publicReadScope = getPublicPostReadScope(post.id);
    const scopes = authData.scopes ?? [];
    if (scopeMatches({ scopes, requiredScope: publicReadScope })) {
        return authData;
    }
    return { ...authData, scopes: [...scopes, publicReadScope] };
};
export const getPublicGroupReadScope = (groupId) => ({
    scopeLevel: 'read',
    resource: { type: 'groups', id: groupId },
});
/**
 * Groups that grant the needed *-read caps to relationship `none` are readable
 * without auth (same pattern as {@link withPublicPostReadScopes}).
 */
export const withPublicGroupReadScopes = (authData, group, neededCapabilities) => {
    if (!neededCapabilities.every((c) => c.endsWith('-read'))) {
        return authData;
    }
    const noneAdd = group.capabilities?.none?.add ?? [];
    if (!neededCapabilities.every((c) => noneAdd.includes(c))) {
        return authData;
    }
    const publicReadScope = getPublicGroupReadScope(group.id);
    const scopes = authData.scopes ?? [];
    if (scopeMatches({ scopes, requiredScope: publicReadScope })) {
        return authData;
    }
    return { ...authData, scopes: [...scopes, publicReadScope] };
};
export const getPublicProfileReadScope = (profileId) => ({
    scopeLevel: 'read',
    resource: { type: 'profiles', id: profileId },
});
/**
 * Profiles whose capabilities config grants the needed *-read caps to
 * relationship `none` are readable without auth (same pattern as
 * {@link withPublicGroupReadScopes}). Community-wide `publicContent` is
 * still enforced by `ensureAccess` on the profile endpoints.
 */
export const withPublicProfileReadScopes = (authData, profile, neededCapabilities, config) => {
    if (!neededCapabilities.every((c) => c.endsWith('-read'))) {
        return authData;
    }
    const noneAdd = config.profile?.none?.add ?? [];
    if (!neededCapabilities.every((c) => noneAdd.includes(c))) {
        return authData;
    }
    const publicReadScope = getPublicProfileReadScope(profile.id);
    const scopes = authData.scopes ?? [];
    if (scopeMatches({ scopes, requiredScope: publicReadScope })) {
        return authData;
    }
    return { ...authData, scopes: [...scopes, publicReadScope] };
};
